/**
 * SubscriptionGuard — blocked companies (none/locked) cannot render contractor shell routes.
 * Grace/active users pass through; paywall overlay in V3Shell remains a fallback only.
 */

import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { fetchBillingStatus } from '../../api/billing'
import { useAuth, isVendor } from '../../context/AuthContext'

interface SubscriptionGuardProps {
  children: ReactNode
}

export default function SubscriptionGuard({ children }: SubscriptionGuardProps) {
  const { user, loading: authLoading } = useAuth()
  const location = useLocation()

  const { data: billing, isLoading: billingLoading, isError } = useQuery({
    queryKey: ['billing-status'],
    queryFn: fetchBillingStatus,
    enabled: !!user && !isVendor(user),
    staleTime: 60_000,
    retry: false,
  })

  if (authLoading || (user && !isVendor(user) && billingLoading)) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'var(--bg)',
      }}>
        <div className="auth-spinner" />
      </div>
    )
  }

  if (user && !isVendor(user) && !isError && billing?.blocked) {
    return <Navigate to="/pricing" replace state={{ from: location.pathname }} />
  }

  return <>{children}</>
}
